<template>
  <div class="rich-editor border border-gray-300 rounded-lg overflow-hidden">
    <!-- Toolbar -->
    <div v-if="editor" class="flex flex-wrap gap-0.5 p-2 bg-gray-50 border-b border-gray-200">
      <button type="button" @click="editor.chain().focus().toggleBold().run()"
              :class="btnClass(editor.isActive('bold'))" title="Bold">
        <strong>B</strong>
      </button>
      <button type="button" @click="editor.chain().focus().toggleItalic().run()"
              :class="btnClass(editor.isActive('italic'))" title="Italic">
        <em>I</em>
      </button>
      <button type="button" @click="editor.chain().focus().toggleUnderline().run()"
              :class="btnClass(editor.isActive('underline'))" title="Underline">
        <span class="underline">U</span>
      </button>
      <button type="button" @click="editor.chain().focus().toggleStrike().run()"
              :class="btnClass(editor.isActive('strike'))" title="Strikethrough">
        <span class="line-through">S</span>
      </button>

      <span class="w-px h-6 bg-gray-300 mx-1"></span>

      <button type="button" @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
              :class="btnClass(editor.isActive('heading', { level: 2 }))" title="Heading">
        H2
      </button>
      <button type="button" @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
              :class="btnClass(editor.isActive('heading', { level: 3 }))" title="Subheading">
        H3
      </button>

      <span class="w-px h-6 bg-gray-300 mx-1"></span>

      <button type="button" @click="editor.chain().focus().toggleBulletList().run()"
              :class="btnClass(editor.isActive('bulletList'))" title="Bullet list">
        &#8226;
      </button>
      <button type="button" @click="editor.chain().focus().toggleOrderedList().run()"
              :class="btnClass(editor.isActive('orderedList'))" title="Numbered list">
        1.
      </button>
      <button type="button" @click="editor.chain().focus().toggleBlockquote().run()"
              :class="btnClass(editor.isActive('blockquote'))" title="Quote">
        &#8220;
      </button>

      <span class="w-px h-6 bg-gray-300 mx-1"></span>

      <button type="button" @click="setLink" :class="btnClass(editor.isActive('link'))" title="Link">
        &#128279;
      </button>
      <button type="button" @click="addImage" :disabled="uploading"
              class="px-2 py-1 text-xs rounded hover:bg-gray-200 text-gray-700 cursor-pointer" title="Upload image">
        {{ uploading ? '...' : '&#128247;' }}
      </button>
      <button type="button" @click="addImageByUrl"
              class="px-2 py-1 text-xs rounded hover:bg-gray-200 text-gray-700 cursor-pointer" title="Image URL">
        &#127760;
      </button>

      <span class="w-px h-6 bg-gray-300 mx-1"></span>

      <!-- Image float controls -->
      <button type="button" @click="setImageFloat('left')"
              :class="btnClass(isImageFloat('left'))" :title="t('news.float_wrap_left')">
        &#9664;&#9776;
      </button>
      <button type="button" @click="setImageFloat('none')"
              :class="btnClass(isImageFloat('none'))" :title="t('news.float_wrap_none')">
        &#9632;
      </button>
      <button type="button" @click="setImageFloat('right')"
              :class="btnClass(isImageFloat('right'))" :title="t('news.float_wrap_right')">
        &#9776;&#9654;
      </button>

      <span class="w-px h-6 bg-gray-300 mx-1"></span>

      <button type="button" @click="editor.chain().focus().setTextAlign('left').run()"
              :class="btnClass(editor.isActive({ textAlign: 'left' }))" title="Align left">
        &#8676;
      </button>
      <button type="button" @click="editor.chain().focus().setTextAlign('center').run()"
              :class="btnClass(editor.isActive({ textAlign: 'center' }))" title="Align center">
        &#8596;
      </button>
      <button type="button" @click="editor.chain().focus().setTextAlign('right').run()"
              :class="btnClass(editor.isActive({ textAlign: 'right' }))" title="Align right">
        &#8677;
      </button>

      <span class="w-px h-6 bg-gray-300 mx-1"></span>

      <button type="button" @click="editor.chain().focus().setHorizontalRule().run()"
              class="px-2 py-1 text-xs rounded hover:bg-gray-200 text-gray-700 cursor-pointer" title="Horizontal rule">
        &#8212;
      </button>
      <button type="button" @click="editor.chain().focus().undo().run()"
              class="px-2 py-1 text-xs rounded hover:bg-gray-200 text-gray-700 cursor-pointer" title="Undo">
        &#8617;
      </button>
      <button type="button" @click="editor.chain().focus().redo().run()"
              class="px-2 py-1 text-xs rounded hover:bg-gray-200 text-gray-700 cursor-pointer" title="Redo">
        &#8618;
      </button>

      <span class="w-px h-6 bg-gray-300 mx-1"></span>

      <button type="button" @click="toggleHtml"
              :class="btnClass(htmlMode)" title="HTML">
        &lt;/&gt;
      </button>
    </div>


    <!-- Visual editor -->
    <div v-if="!htmlMode" class="p-3 cursor-text" @click="editor?.commands.focus()" style="min-height: 200px;">
      <editor-content :editor="editor" class="prose prose-sm max-w-none focus:outline-none" />
    </div>

    <!-- HTML source editor -->
    <textarea v-else v-model="htmlSource" @input="onHtmlInput"
              class="w-full p-3 text-sm font-mono text-gray-700 focus:outline-none" style="min-height: 200px; resize: vertical;"></textarea>

    <!-- Hidden file input -->
    <input ref="fileInput" type="file" accept="image/*" class="hidden" @change="onFileSelected" />
  </div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import { NodeSelection } from '@tiptap/pm/state'
import StarterKit from '@tiptap/starter-kit'
import ImageResize from 'tiptap-extension-resize-image'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Placeholder from '@tiptap/extension-placeholder'

const { t } = useI18n()


const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  csrfToken: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue'])
const fileInput = ref(null)
const htmlMode = ref(false)
const htmlSource = ref('')
const uploading = ref(false)

const editor = useEditor({
  content: props.modelValue,
  shouldRerenderOnTransaction: true,
  extensions: [
    StarterKit,
    Underline,
    ImageResize.configure({
      inline: false,
      allowBase64: true,
      minWidth: 80,
      maxWidth: 1100
    }),
    Link.configure({ openOnClick: false }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Placeholder.configure({ placeholder: props.placeholder })
  ],
  onUpdate: ({ editor }) => {
    emit('update:modelValue', editor.getHTML())
  }
})

watch(() => props.modelValue, (val) => {
  if (editor.value && editor.value.getHTML() !== val) {
    editor.value.commands.setContent(val, false)
  }
})

onBeforeUnmount(() => {
  if (editor.value) editor.value.destroy()
})

function toggleHtml() {
  if (htmlMode.value) {
    editor.value.commands.setContent(htmlSource.value, false)
    emit('update:modelValue', htmlSource.value)
  } else {
    htmlSource.value = editor.value.getHTML()
  }
  htmlMode.value = !htmlMode.value
}

function onHtmlInput() {
  emit('update:modelValue', htmlSource.value)
}

function btnClass(active) {
  return ['px-2 py-1 text-xs rounded cursor-pointer', active ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-200 text-gray-700']
}

function setLink() {
  const url = window.prompt('URL:')
  if (url) {
    editor.value.chain().focus().setLink({ href: url }).run()
  } else {
    editor.value.chain().focus().unsetLink().run()
  }
}

function addImage() {
  fileInput.value.click()
}

function addImageByUrl() {
  const url = window.prompt('Image URL:')
  if (url) {
    editor.value.chain().focus().setImage({ src: url }).run()
  }
}

/** Matches ImageResize sanitizer: float + display + margin + max-width only. */
const WRAPPER_FLOAT_LEFT =
  'display: inline-block; float: left; margin: 0 1rem 0.75rem 0; max-width: 55%;'
const WRAPPER_FLOAT_RIGHT =
  'display: inline-block; float: right; margin: 0 0 0.75rem 1rem; max-width: 55%;'
const WRAPPER_BLOCK = 'display: flex; margin: 0;'

function selectedImageResizeNode() {
  const ed = editor.value
  if (!ed) return null
  const sel = ed.state.selection
  if (sel instanceof NodeSelection && sel.node.type.name === 'imageResize') {
    return { pos: sel.from, node: sel.node }
  }
  return null
}

function setImageFloat(side) {
  if (!selectedImageResizeNode()) {
    window.alert(t('news.select_image_first'))
    return
  }
  const wrapperStyle =
    side === 'left' ? WRAPPER_FLOAT_LEFT : side === 'right' ? WRAPPER_FLOAT_RIGHT : WRAPPER_BLOCK
  editor.value.chain().focus().updateAttributes('imageResize', { wrapperStyle }).run()
}

function isImageFloat(side) {
  const picked = selectedImageResizeNode()
  if (!picked) return false
  const ws = picked.node.attrs.wrapperStyle || ''
  if (side === 'left') return /float:\s*left/i.test(ws)
  if (side === 'right') return /float:\s*right/i.test(ws)
  return !/float:\s*(left|right)/i.test(ws)
}

async function onFileSelected(event) {
  const file = event.target.files[0]
  if (!file) return
  event.target.value = ''

  if (props.csrfToken) {
    uploading.value = true
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', 'news')
      const res = await fetch('/upload', {
        method: 'POST',
        headers: { 'X-CSRF-Token': props.csrfToken },
        body: formData
      })
      if (res.ok) {
        const data = await res.json()
        editor.value.chain().focus().setImage({ src: data.url }).run()
      }
    } catch {}
    uploading.value = false
  } else {
    const reader = new FileReader()
    reader.onload = (e) => {
      editor.value.chain().focus().setImage({ src: e.target.result }).run()
    }
    reader.readAsDataURL(file)
  }
}
</script>

<style>
.rich-editor .tiptap {
  outline: none;
  min-height: 180px;
}
.rich-editor .tiptap::after {
  content: '';
  display: block;
  clear: both;
}
.rich-editor .tiptap p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}
.rich-editor .tiptap img {
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
  margin: 0.5rem 0;
  cursor: pointer;
  vertical-align: bottom;
}
.rich-editor .tiptap img.ProseMirror-selectednode {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}
.rich-editor .tiptap h2 { font-size: 1.25rem; font-weight: 700; margin: 0.75rem 0 0.25rem; }
.rich-editor .tiptap h3 { font-size: 1.1rem; font-weight: 600; margin: 0.5rem 0 0.25rem; }
.rich-editor .tiptap blockquote { border-left: 3px solid #d1d5db; padding-left: 1rem; color: #6b7280; margin: 0.5rem 0; }
.rich-editor .tiptap ul { list-style: disc; padding-left: 1.5rem; }
.rich-editor .tiptap ol { list-style: decimal; padding-left: 1.5rem; }
.rich-editor .tiptap a { color: #2563eb; text-decoration: underline; }
.rich-editor .tiptap hr { border-color: #e5e7eb; margin: 1rem 0; }
</style>
